/**
 * Client-side order edit policy: an order can be edited until 2 business days
 * before its delivery date (mirrors the backend lead-time check).
 */

// One editability rule for every order in the system — client-placed,
// admin-booked or custom. Mirrors backend/services/order_edit_policy.py.
export const NON_EDITABLE_STATUSES = new Set([
  'in_production', 'out_for_delivery', 'delivered', 'failed_delivery', 'cancelled',
]);

const BLOCK_MESSAGES = {
  in_production: 'Already in production — move the status back to Confirmed to edit.',
  out_for_delivery: 'Already out for delivery — move the status back to Confirmed to edit.',
  delivered: 'This order is delivered — a delivered order can no longer be edited.',
  failed_delivery: 'Marked as a failed delivery — move the status back to Confirmed to edit.',
  cancelled: 'This order is cancelled — move the status back to Confirmed to edit.',
};

export const editBlockedMessage = (status) =>
  BLOCK_MESSAGES[status] || `Cannot edit an order with status '${status}'.`;

// Count business days between two dates (exclusive of start, inclusive of end)
export const countBusinessDays = (from, to) => {
  let count = 0;
  let current = new Date(from);
  current.setDate(current.getDate() + 1);
  while (current <= to) {
    if (current.getDay() !== 0 && current.getDay() !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

// Calculate the editing deadline (2 business days before delivery)
export const getEditDeadline = (deliveryDate) => {
  if (!deliveryDate) return null;
  let dt = new Date(deliveryDate);
  dt.setHours(23, 59, 59, 0);
  let bizCount = 0;
  while (bizCount < 2) {
    dt.setDate(dt.getDate() - 1);
    const dow = dt.getDay();
    if (dow !== 0 && dow !== 6) bizCount++;
  }
  return dt;
};

export const canEditOrder = (order) => {
  if (!order || order.status !== 'submitted') return false;
  // An invoiced order can no longer be edited directly — the client submits a
  // change request instead (server is authoritative; this is UX only).
  if (order.invoice_id) return false;
  const deadline = getEditDeadline(order.delivery_date);
  if (!deadline) return false;
  return new Date() <= deadline;
};

// A change request replaces the direct edit once an advance invoice exists.
export const canRequestChange = (order) => {
  if (!order || !order.invoice_id) return false;
  if (order.billing_timing !== 'advance') return false;
  if (!['submitted', 'confirmed'].includes(order.status)) return false;
  if (order.has_pending_change_request) return false;
  const deadline = getEditDeadline(order.delivery_date);
  if (!deadline) return false;
  return new Date() <= deadline;
};
